import React, { useEffect, useMemo, useState } from "react";
import escapeHtml from "lodash/escape";
import Swal from "../../utils/swal";
import { showSuccessToast, showErrorToast } from "../../utils/swalToast";
import apiClient from "../../utils/apiClient";
import { formatDateTimestamp } from "../../utils/date";
import RowActionsMenu from "../custom/RowActionsMenu";
import CustomDataGrid from "../custom/CustomDataGrid";

const safeValue = (val) => {
  if (val === null || val === undefined || val === "" || val === "null") {
    return "-";
  }
  return val;
};

export default function MemberCandidateData() {
  const [page, setPage] = useState(1);
  const [rawRows, setRawRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [countries, setCountries] = useState([]);

  // Fetch provinces & countries sekali di awal
  useEffect(() => {
    const fetchRefs = async () => {
      try {
        const [resProvinces, resCountries] = await Promise.all([
          apiClient.get("/provinces"),
          apiClient.get("/countries"),
        ]);

        setProvinces(resProvinces.data.data || []);
        setCountries(resCountries.data.data || []);
      } catch (err) {
        console.error("Error fetching refs", err);
      }
    };

    fetchRefs();
  }, []);

  // Fetch candidate users — kept independent of provinces/countries so it
  // doesn't refetch a second time once those reference lists arrive; the
  // province/country name lookup below is a client-side join instead.
  useEffect(() => {
    apiClient
      .get("/users/candidate")
      .then((response) => {
        setRawRows(response.data.data);
      })
      .catch((error) => {
        console.error("Error fetching candidate users", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, refresh]);

  const rows = useMemo(
    () =>
      rawRows.map((user, index) => {
        const provinceObj = provinces.find(
          (p) => String(p.id) === String(user.provinsi_institusi)
        );
        const countryObj = countries.find((c) => c.code === user.negara_asal);

        return {
          id: index + 1 + (page - 1) * 10,
          namaLengkap: user.nama_lengkap,
          universitas: safeValue(user.institusi_asal),
          provinsi: provinceObj
            ? provinceObj.name
            : safeValue(user.provinsi_institusi),
          negara: countryObj ? countryObj.name : safeValue(user.negara_asal),
          email: user.email,
          role: user.role,
          keahlian: [user.keahlian_1, user.keahlian_2, user.keahlian_3]
            .filter(Boolean)
            .join(", "),
        };
      }),
    [rawRows, provinces, countries, page]
  );

  const handleUpdateMemberStatus = async (email) => {
    const memberStatus = 1;
    Swal.fire({
      title: "Menerima Calon Anggota",
      text: "Kamu akan menerima calon anggota berikut",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
      confirmButtonText: "Ya, terima",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiClient.put("/users/member", { email, memberStatus });
          showSuccessToast("Calon anggota telah diterima!");
          setRefresh(!refresh);
        } catch (error) {
          console.error("Error updating member status", error);
          showErrorToast("There was an error updating the member status.");
        }
      }
    });
  };

  const handleRejectMember = async (email) => {
    const memberStatus = -1;
    Swal.fire({
      title: "Hapus Calon Anggota",
      text: "Kamu akan menghapus calon anggota berikut",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
      confirmButtonText: "Ya, Hapus",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await apiClient.put("/users/member", { email, memberStatus });
          showSuccessToast("Calon Anggota telah di hapus dari list");
          setRefresh(!refresh);
        } catch (error) {
          console.error("Error updating member status", error);
          showErrorToast("There was an error updating the member status.");
        }
      }
    });
  };

  const handleShowMemberInformation = async (email) => {
    try {
      const response = await apiClient.get(`/users?email=${email}`);

      const userData = response.data.data[0];
      const tanggalLahir = formatDateTimestamp(userData.tanggal_lahir);

      const countryObj = countries.find((c) => c.code === userData.negara_asal);
      const provinceObj = provinces.find(
        (p) => String(p.id) === String(userData.provinsi_institusi)
      );

      const countryName = countryObj
        ? countryObj.name
        : safeValue(userData.negara_asal);
      const provinceName = provinceObj
        ? provinceObj.name
        : safeValue(userData.provinsi_institusi);

      Swal.fire({
        title: "User Information",
        html: `
        <div style="text-align: left;">
          <p><strong>KTP :</strong> ${escapeHtml(userData.ktp)}</p>
          <p><strong>Nama Lengkap :</strong> ${escapeHtml(userData.nama_lengkap)}</p>
          <p><strong>Tempat Lahir :</strong> ${escapeHtml(userData.tempat_lahir)}</p>
          <p><strong>Tanggal Lahir :</strong> ${escapeHtml(tanggalLahir)}</p>
          <p><strong>Negara Asal :</strong> ${escapeHtml(countryName)}</p>
          <p><strong>Provinsi Institusi :</strong> ${escapeHtml(provinceName)}</p>
          <p><strong>Kota Institusi :</strong> ${escapeHtml(safeValue(
            userData.kota_institusi
          ))}</p>
          <p><strong>Institusi Asal :</strong> ${escapeHtml(safeValue(
            userData.institusi_asal
          ))}</p>
          <p><strong>Program Studi :</strong> ${escapeHtml(userData.prodi)}</p>
          <p><strong>Email :</strong> ${escapeHtml(userData.email)}</p>
          <p><strong>Role :</strong> ${escapeHtml(userData.role)}</p>
        </div>
      `,
        icon: "info",
      });
    } catch (error) {
      console.error("Error fetching user information", error);
      showErrorToast("There was an error fetching user information.");
    }
  };

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <CustomDataGrid
        rows={rows}
        columns={[
          { field: "id", headerName: "No.", flex: 1 },
          { field: "namaLengkap", headerName: "Nama Lengkap", flex: 1 },
          { field: "universitas", headerName: "Universitas", flex: 1 },
          { field: "provinsi", headerName: "Provinsi", flex: 1 },
          { field: "keahlian", headerName: "Keahlian", flex: 1 },
          { field: "role", headerName: "Role", flex: 1 },
          {
            field: "updateMemberStatus",
            headerName: "Action",
            width: 120,
            renderCell: (params) => (
              <RowActionsMenu
                actions={[
                  {
                    label: "Cek Informasi Anggota",
                    onClick: () => handleShowMemberInformation(params.row.email),
                  },
                  {
                    label: "Terima Anggota",
                    onClick: () => handleUpdateMemberStatus(params.row.email),
                  },
                  {
                    label: "Tolak Anggota",
                    onClick: () => handleRejectMember(params.row.email),
                    danger: true,
                  },
                ]}
              />
            ),
          },
        ]}
        setPage={setPage}
        loading={loading}
      />
    </div>
  );
}
